import { useState, useEffect, useCallback } from 'react';
import yjsDocumentService from '../../infrastructure/sync/YjsDocumentService';
import { getDeviceInfo } from '../../infrastructure/storage/localStorage';
import { getWorkspaceId } from '../services/dbUtils';

/**
 * A hook to manage user settings stored in a YJS document
 * Settings are now stored in a common 'userSettings' map with each device's
 * settings stored under its own GUID as a key
 * 
 * @returns {Object} - User settings state and methods
 */
export const useUserSettings = () => {
  const [settings, setSettings] = useState({});
  const [allDeviceSettings, setAllDeviceSettings] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const workspaceId = getWorkspaceId();
  const deviceInfo = getDeviceInfo();

  // Load settings on initial render (only this device's settings)
  useEffect(() => {
    if (workspaceId) {
      try {
        const userSettings = yjsDocumentService.getUserSettings(workspaceId);
        setSettings(userSettings);
        
        // Also load all device settings
        const allSettings = yjsDocumentService.getAllUserSettings(workspaceId);
        setAllDeviceSettings(allSettings);
        
        setIsLoaded(true);
      } catch (error) {
        console.error('Failed to load user settings:', error);
        setIsLoaded(true); // Still mark as loaded even on error
      }
    }
  }, [workspaceId]);

  // Update a single setting for current device
  const updateSetting = useCallback((key, value) => {
    if (!workspaceId) {
      console.error('Cannot update settings: Workspace ID is not available');
      return false;
    }

    try {
      const success = yjsDocumentService.updateUserSettings(workspaceId, { [key]: value });
      if (success) {
        setSettings(prev => ({ ...prev, [key]: value }));
      }
      return success;
    } catch (error) {
      console.error(`Failed to update setting ${key}:`, error);
      return false;
    }
  }, [workspaceId]);

  // Update multiple settings at once for current device
  const updateSettings = useCallback((newSettings) => {
    if (!workspaceId) {
      console.error('Cannot update settings: Workspace ID is not available');
      return false;
    }

    try {
      const success = yjsDocumentService.updateUserSettings(workspaceId, newSettings);
      if (success) {
        setSettings(prev => ({ ...prev, ...newSettings }));
      }
      return success;
    } catch (error) {
      console.error('Failed to update settings:', error);
      return false;
    }
  }, [workspaceId]);

  // Get settings for a specific device
  const getDeviceSettings = useCallback((deviceId) => {
    if (!deviceId || !allDeviceSettings) return null;
    return allDeviceSettings[deviceId] || null;
  }, [allDeviceSettings]);

  // Subscribe to changes in settings (from any client)
  useEffect(() => {
    if (!workspaceId) return;
    
    // Subscribe to document updates
    const unsubscribe = yjsDocumentService.subscribeToDocumentEvents(workspaceId, (event) => {
      if (event.type === 'document-update') {
        // When document updates, refresh our device's settings
        const refreshedSettings = yjsDocumentService.getUserSettings(workspaceId);
        setSettings(refreshedSettings);
        
        // Also refresh all devices' settings
        const allSettings = yjsDocumentService.getAllUserSettings(workspaceId);
        setAllDeviceSettings(allSettings);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [workspaceId]);

  return {
    settings,              // Current device's settings
    isLoaded,              // Whether settings have loaded
    updateSetting,         // Update a single setting
    updateSettings,        // Update multiple settings
    deviceId: deviceInfo.guid,  // Current device ID
    allDeviceSettings,     // All devices' settings as a mapping: { [deviceId]: settingsObj }
    getDeviceSettings      // Helper function to get a specific device's settings
  };
};

export default useUserSettings;